import { ibkrQuoteStream, normalizeQuoteContract } from './ibkrQuoteSubscriptionStore.js'

function normalizeSnapshotRequest(symbol, params = {}) {
  const hasExplicitExchange = String(params.exchange || '').trim() !== ''
  return normalizeQuoteContract({
    symbol,
    ...params,
    // AUTO is a logical browser identity. The quote stream resolves it to
    // SMART or OVERNIGHT without splitting UI consumers into separate stores.
    exchange: hasExplicitExchange ? params.exchange : 'AUTO',
  })
}

export function createIbkrQuoteSnapshotStore({ stream = ibkrQuoteStream } = {}) {
  const entries = new Map()
  const storeListeners = new Set()
  let version = 0

  const notify = (entry, snapshot) => {
    version += 1
    for (const listener of entry.listeners) {
      try { listener(snapshot) } catch { /* isolate sibling consumers */ }
    }
    for (const listener of storeListeners) {
      try { listener() } catch { /* isolate sibling consumers */ }
    }
  }

  const getEntry = (symbol, params) => entries.get(normalizeSnapshotRequest(symbol, params).key)

  function retain(symbol, params = {}, listener = () => {}) {
    const normalized = normalizeSnapshotRequest(symbol, params)
    let entry = entries.get(normalized.key)
    if (!entry) {
      entry = {
        key: normalized.key,
        symbol: normalized.request.symbol,
        params: { ...params },
        listeners: new Set(),
        snapshot: null,
        release: null,
        releasePending: false,
      }
      entries.set(entry.key, entry)

      const handleSnapshot = snapshot => {
        if (snapshot == null) {
          if (entry.snapshot == null) return
          entry.snapshot = null
          notify(entry, null)
          return
        }
        // The authenticated subscription store is the sole sequence gate. Do
        // not repeat that gate here: recovery snapshots may intentionally
        // reuse/reset a sequence, and stale-disconnect snapshots keep the
        // provider sequence while changing freshness.
        entry.snapshot = Object.freeze(snapshot)
        notify(entry, entry.snapshot)
      }

      const release = stream.subscribeSnapshot(entry.symbol, handleSnapshot, entry.params)
      if (release && typeof release.then === 'function') {
        release.then(resolved => {
          if (entry.releasePending) resolved?.()
          else entry.release = resolved
        }).catch(() => {})
      } else {
        entry.release = release
      }
    }

    entry.listeners.add(listener)
    if (entry.snapshot) {
      try { listener(entry.snapshot) } catch { /* isolate this consumer */ }
    }

    let released = false
    return () => {
      if (released) return
      released = true
      entry.listeners.delete(listener)
      if (entry.listeners.size > 0) return
      entries.delete(entry.key)
      if (entry.release) entry.release()
      else entry.releasePending = true
    }
  }

  return {
    retain,
    getSnapshot(symbol, params = {}) {
      return getEntry(symbol, params)?.snapshot ?? null
    },
    getVersion: () => version,
    subscribe(listener) {
      storeListeners.add(listener)
      return () => storeListeners.delete(listener)
    },
  }
}

export const ibkrQuoteSnapshotStore = createIbkrQuoteSnapshotStore()
